import { getCryptoCollectionPage } from '@/api/market';
import { resolveOrderSymbol } from '@/utils/trade-order';
import {
  CRYPTO_CURRENCY_SLUGS,
  CRYPTO_PRICE_SLUG_MAP,
  isCryptoCurrencyFilter,
} from '@/utils/crypto-sidebar-map';
import { getFirstListedMarket } from '@/utils/market-list-status.js';
import { createFeedSourceAdapter } from '@/domain/market/feed-source-adapter.js';
import { mapFeedCardFromRow } from '@/domain/market/feed-card-model.js';
import {
  normalizeCryptoMinuteUpdownFeedRows,
  parseMinuteUpdownSlug,
  resolveUpdownRoundSlugSource,
} from '@/domain/crypto/crypto-minute-slug.js';
import { fetchPagesUntilUpdownNormalized } from '@/domain/crypto/crypto-minute-feed-pagination.js';
import {
  fetchCryptoGroupBySlugRows,
  toGroupBootstrapPageResult,
} from '@/domain/crypto/crypto-group-by-slug-feed.js';
import { shouldKeepCryptoFeedRowBySource } from '@/domain/crypto/crypto-feed-source.js';

export const CRYPTO_FEED_PAGE_SIZE = 21;

/**
 * 自建短周期切流后，加密频道不展示 PM 的 updown 轮次。
 * 普通 PM 加密事件原样保留；缺 dataSource 时保守保留，避免误删未知来源。
 */
export function normalizeCryptoFeedRowsForDisplay(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const selfBuiltOnly = list.filter((row) => {
    const parsed = parseMinuteUpdownSlug(resolveUpdownRoundSlugSource(row));
    return shouldKeepCryptoFeedRowBySource(row, Boolean(parsed));
  });
  return normalizeCryptoMinuteUpdownFeedRows(selfBuiltOnly);
}

const CURRENCY_DISPLAY = {
  bitcoin: 'Bitcoin',
  ethereum: 'Ethereum',
  solana: 'Solana',
  xrp: 'XRP',
  dogecoin: 'Dogecoin',
  bnb: 'BNB',
  microstrategy: 'MicroStrategy',
};

const CURRENCY_ACCENT = {
  bitcoin: '#f7931a',
  ethereum: '#627eea',
  solana: '#9945ff',
  xrp: '#23292f',
  dogecoin: '#c2a633',
  bnb: '#f0b90b',
  microstrategy: '#f7931a',
};

function inferCurrencySlug(row, activeFilter) {
  if (isCryptoCurrencyFilter(activeFilter)) return String(activeFilter).trim().toLowerCase();
  const direct = row.currencySlug || row.currency;
  if (direct) return String(direct).trim().toLowerCase();
  const tags = Array.isArray(row.tags) ? row.tags : [];
  const matched = tags.find((tag) =>
    CRYPTO_CURRENCY_SLUGS.has(String(tag.slug || '').toLowerCase())
  );
  if (matched?.slug) return String(matched.slug).toLowerCase();
  return '';
}

/** collection row → Crypto 列表卡片 view model（variant 与首页一致，含已结算 complete / 空 markets） */
export function mapCryptoFeedRow(row, activeFilter, locale = 'zh') {
  const card = mapFeedCardFromRow(row, locale);
  if (!card?.id) return null;

  const market = getFirstListedMarket(row?.markets);
  const slug = inferCurrencySlug(row, activeFilter);
  const tags = Array.isArray(row.tags) ? row.tags : [];
  const tagLabel = tags[0]?.label || row.ticker;
  const coinTag = tagLabel || CURRENCY_DISPLAY[slug] || row.ticker || 'Crypto';

  return {
    ...card,
    orderSymbol: card.orderSymbol || resolveOrderSymbol({ collection: row, market }),
    coinTag,
    accentColor: CURRENCY_ACCENT[slug] || '#64748b',
    liquidityValue: card.volume,
    footerTag: coinTag,
    footerTagVariant: 'crypto',
    outcomesRaw: market?.outcomes ?? card.outcomesRaw,
    /** 侧栏周期：供详情识别日/周/月/年现货长图 */
    cryptoPriceSlug: CRYPTO_PRICE_SLUG_MAP[activeFilter] || '',
    cryptoFilterKey: String(activeFilter ?? '').trim(),
  };
}

export function buildCryptoFeedQuery(store) {
  const query = {};
  const filter = String(store.selectedFilter ?? '').trim();

  if (isCryptoCurrencyFilter(filter)) {
    query.currencySlug = filter;
  } else if (CRYPTO_PRICE_SLUG_MAP[filter]) {
    query.priceSlug = CRYPTO_PRICE_SLUG_MAP[filter];
  }

  return query;
}

/** 首屏：cryptoPageGroupBySlug（同 slug 前缀一条） */
export async function fetchCryptoFeedGroupPageResult(ctx) {
  const { query } = ctx;
  const rows = await fetchCryptoGroupBySlugRows(query ?? {});
  return toGroupBootstrapPageResult(rows, normalizeCryptoFeedRowsForDisplay);
}

/** 静默 / loadMore：cryptoPage + 客户端归集 */
export async function fetchCryptoFeedPageResult(ctx) {
  const { append, pageNum, pageSize = CRYPTO_FEED_PAGE_SIZE, query, state } = ctx;
  const startPageNum = append ? pageNum + 1 : 1;
  const baselineRows = append ? state?.rawRows ?? [] : [];

  return fetchPagesUntilUpdownNormalized({
    pageSize,
    startPageNum,
    append,
    baselineRows,
    normalizeRows: normalizeCryptoFeedRowsForDisplay,
    fetchRawPage: async (page) => {
      const data = await getCryptoCollectionPage(query, { pageNum: page, pageSize });
      return { data, rows: Array.isArray(data?.rows) ? data.rows : [] };
    },
  });
}

export function createCryptoFeedFetchOptions() {
  return createFeedSourceAdapter({
    logTag: 'cryptoFeed',
    pageSize: CRYPTO_FEED_PAGE_SIZE,
    buildQuery: buildCryptoFeedQuery,
    fetchPageResult: fetchCryptoFeedPageResult,
  });
}

export function createCryptoFeedGroupFetchOptions() {
  return createFeedSourceAdapter({
    logTag: 'cryptoFeed',
    pageSize: CRYPTO_FEED_PAGE_SIZE,
    buildQuery: buildCryptoFeedQuery,
    fetchPageResult: fetchCryptoFeedGroupPageResult,
  });
}
